import * as React from "react";

import { useAuth } from "@/providers";
import {
  useSearchTablePreferences,
  useUpdateSearchTablePreferences,
} from "@/shared/domain/config/hooks";
import { searchTablePreferenceKeyFor } from "@/shared/domain/config/searchTablePreferenceKeys";
import { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";

export interface DefaultView {
  /** The View this customer starts this table from, or `null` for none. */
  defaultViewId: string | null;
  /** Whether `view` is that default. */
  isDefault: (view: ViewRecord) => boolean;
  /** Start this table from `view` in future. Replaces any existing default. */
  setDefault: (view: ViewRecord) => Promise<void>;
  /** Stop starting this table from anything. */
  clearDefault: () => Promise<void>;
  /** True until the stored default has been read. */
  isPending: boolean;
  /** True while a set or clear is in flight. */
  isSaving: boolean;
}

/**
 * The View a customer starts one table from.
 *
 * Stored on the existing per-user table-preferences record rather than
 * somewhere of its own, because that record is already keyed the way a default
 * needs to be — per user, per table, and org-scoped within — and it is already
 * the place that answers "how does this person like this table".
 *
 * The id is a hint, not a guarantee. Nothing keeps it in step with the `views`
 * table, so the View it names may since have been deleted or stopped being
 * visible. Callers resolve it and fall back rather than trusting it.
 */
export function useDefaultView(target: QueryTarget): DefaultView {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id ?? null;
  const configKey = searchTablePreferenceKeyFor(target);

  const preferences = useSearchTablePreferences(configKey);
  const update = useUpdateSearchTablePreferences(configKey);

  const defaultViewId =
    orgId === null
      ? null
      : (preferences.data?.org_scoped_preferences[orgId]?.default_view_id ??
        null);

  const updateAsync = update.mutateAsync;

  const write = React.useCallback(
    async (nextId: string | null) => {
      if (orgId === null) {
        throw new Error("Cannot set a default View with no current org");
      }
      // Only the field being changed. The server merges, so sending the whole
      // record would make the customer's column arrangement collateral damage
      // of starring a View. `null` is what clears it — an omitted key means
      // "leave unchanged", so an unset cannot be expressed by absence.
      await updateAsync({ orgId, preferences: { default_view_id: nextId } });
    },
    [orgId, updateAsync],
  );

  const setDefault = React.useCallback(
    (view: ViewRecord) => write(view.view_id),
    [write],
  );

  const clearDefault = React.useCallback(() => write(null), [write]);

  const isDefault = React.useCallback(
    (view: ViewRecord) =>
      defaultViewId !== null && view.view_id === defaultViewId,
    [defaultViewId],
  );

  return {
    defaultViewId,
    isDefault,
    setDefault,
    clearDefault,
    isPending: preferences.isPending,
    isSaving: update.isPending,
  };
}
